import { gql } from '@apollo/client';
import { CollectionPriorityFields } from '../collection/fragments';
import {
  AutomaticRiskEvaluationResultFields,
  PreofferEvaluationInfo,
  SLAResolutionFields,
} from '../evaluation_requests/fragments';
import { BasicUserFields } from '../fragments';
import CompanyEvaluationRestrictionMinimalFields from '../fragments/company-evaluation-restriction/company-evaluation-restriction-minimal-fields';
import MoneyFields from '../fragments/money/money-fields';
import {
  InvoiceLoanManagerBasicField,
  InvoiceLoanRosterBasic,
} from '../invoice-loan/fragments';
import {
  AssignmentFields,
  DocumentFinanceStateFields,
  InvoiceAlertsFields,
  InvoiceDocumentsFields,
  InvoiceDteTypeFields,
  InvoiceIdFields,
  InvoicePreofferEvaluationRequestTypeFields,
  InvoiceReferenceFields,
  InvoiceRequestingPlatformFields,
  InvoiceStatusFields,
  TraceFields,
} from '../invoices/fragments';
import { InvoiceReviewDocumentsFields } from '../fragments/invoice/invoice-review-documents-fields';
import { RatificationActionsBasicFields } from '../ratification/fragments';
import { UserIdentification } from '../user/fragments';
import {
  CompanyDebtsSummary,
  CompanyDocumentsFields,
  CompanyReceiverEvaluationCommentFields,
  CompanyRestrictions,
  CredentialFields,
  CreditLineEvaluationRequestFields,
  CreditLineFields,
  CreditLineMovementFields,
  CreditLineSuggestionTypeFields,
  CreditLineTransferTypeFields,
  EvaluationCommentFields,
  MasterEntityBasicField,
  MasterEntityPublicInfoType,
  TermWhitelistFields,
  ReceiverDebtsSummary,
  RiskListFields,
  RiskListCategoryFields,
  CreditlineEvaluationVariablesTypeFields,
  CreditlineEvaluationIndicatorsTypeFields,
  MasterEntityExternalRiskStatusFields,
  CreditLineInfoExternalRiskStatusFields,
} from './fragment';
import CollectionMasterEntityFragment from '../fragments/collection-master-entity';
import {
  PendingCollectionManagerBasicFragment,
  PendingCollectionManagerActionsFragment,
} from '../fragments/pending-collection-manager';

export const GET_REQUESTING_PLATFORMS = gql`
  query getRequestingPlatforms {
    getRequestingPlatforms {
      id
      code
    }
  }
`;

export const GET_REQUESTING_PLATFORM = gql`
  query getRequestingPlatform($id: String!) {
    getRequestingPlatform(id: $id) {
      id
      code
    }
  }
`;

export const GET_BANK_ACCOUNTS = gql`
  query GetCompanyBankAccounts($companyIds: [Int]!) {
    getCompanyBankAccounts(companyIds: $companyIds) {
      id
      accountEmail
      accountName
      accountNumber
      accountRut
      accountType
      bankName
      active
    }
  }
`;

export const GET_BANK_ACCOUNT_TYPES = gql`
  query getBankAccountTypesByCountry($countryId: String!) {
    getBankAccountTypesByCountry(countryId: $countryId) {
      id
      name
    }
  }
`;

export const GET_BANKS = gql`
  query getBankOptionsByCountry($countryId: String!) {
    getBankOptionsByCountry(countryId: $countryId) {
      id
      name
    }
  }
`;

export const GET_NATIONAL_INVOICE_PROVIDER = gql`
  query getNationalInvoiceProvider($countryId: String!) {
    getNationalInvoiceProvider(countryId: $countryId) {
      id
      name
    }
  }
`;

export const PROVIDER_EXCEL_TEMPLATE = gql`
  query exportTemplateContacts {
    exportTemplateContacts
  }
`;

export const TERMS_AND_CONDITIONS = gql`
  query TermsAndConditions($countryId: Int!) {
    termsAndConditions(last: 1, countryId: $countryId) {
      edges {
        node {
          id
          sections {
            id
            text
            sectionType
          }
        }
      }
    }
  }
`;

export const ORDERING_DEBTOR_SUMMARY = gql`
  query orderingDebtorSummary($debtorId: String!) {
    operationsSummary(debtorId: $debtorId) {
      debtCount
      debtAmount
      currentValid
      currentAmount
    }
  }
`;

export const GET_MASTER_ENTITY_TITLE = gql`
  query masterEntityTitle($masterEntityId: Int!) {
    getMasterEntity(masterEntityId: $masterEntityId) {
      id
      name
      displayNationalIdentifier
    }
  }
`;

export const GET_MASTER_ENTITY = gql`
  query masterEntity($masterEntityId: Int!) {
    getMasterEntity(masterEntityId: $masterEntityId) {
      id
      rut
      name
      debtor {
        id
        classification {
          id
          code
        }
      }
      contacts(first: 1, showHidden: false) {
        edges {
          node {
            id
            name
            position
            email
            phoneNumber
          }
        }
      }
      company {
        id
        rut
        documents {
          ...CompanyDocumentsFields
        }
      }
    }
  }
  ${CompanyDocumentsFields}
`;

const FACTORING_COLLECTION_MASTER_ENTITIES_BASE_QUERY = (fragmentName, queryName, entityFragmentName) => `
  query ${queryName}(
    $first: Int
    $offset: Int
    $globalFilter: String
    $id_In: [String]
    $orderBy: String
    $inCollection: Int
    $debtor_CollectionPriorities_Priority_Value: Int
    $countryId: Int
  ) {
    getMasterEntities(
      first: $first
      offset: $offset
      globalFilter: $globalFilter
      id_In: $id_In
      inFactoringCollection: $inCollection
      orderBy: $orderBy
      debtor_CollectionPriorities_Priority_Value: $debtor_CollectionPriorities_Priority_Value
      countryId: $countryId
    ) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
      totalPages
      edges {
        cursor
        node {
          ${entityFragmentName ? `...${entityFragmentName}` : 'id'}
          pendingCollectionManagers: pendingFactoringCollectionManagers {
            ...${fragmentName}
          }
        }
      }
    }
  }
`;

export const FACTORING_COLLECTION_MASTER_ENTITIES = gql`
  ${FACTORING_COLLECTION_MASTER_ENTITIES_BASE_QUERY(
    'PendingCollectionManagerBasicFragment',
    'factoringCollectionMasterEntities',
    'CollectionMasterEntityFragment',
  )}
  ${CollectionMasterEntityFragment}
  ${PendingCollectionManagerBasicFragment}
`;

export const GET_PENDING_COLLECTION_MANAGERS_ACTIONS = gql`
  ${FACTORING_COLLECTION_MASTER_ENTITIES_BASE_QUERY(
    'PendingCollectionManagerActionsFragment',
    'factoringCollectionManagersActions',
  )}
  ${PendingCollectionManagerActionsFragment}
`;

const ORDERING_COLLECTION_MASTER_ENTITIES_BASE_QUERY = (queryName, fragmentName, entityFragmentName) => `
  query ${queryName}(
    $first: Int
    $offset: Int
    $globalFilter: String
    $id_In: [String]
    $orderBy: String
    $inCollection: Int
    $debtor_CollectionPriorities_Priority_Value: Int
    $countryId: Int
  ) {
    getMasterEntities(
      id_In: $id_In
      first: $first
      offset: $offset
      globalFilter: $globalFilter
      inOrderingCollection: $inCollection
      orderBy: $orderBy
      debtor_CollectionPriorities_Priority_Value: $debtor_CollectionPriorities_Priority_Value
      countryId: $countryId
    ) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
      totalPages
      edges {
        cursor
        node {
          ${entityFragmentName ? `...${entityFragmentName}` : 'id'}
          pendingCollectionManagers: pendingOrderingCollectionManagers {
            ...${fragmentName}
          }
        }
      }
    }
  }
`;

export const ORDERING_COLLECTION_MASTER_ENTITIES = gql`
  ${ORDERING_COLLECTION_MASTER_ENTITIES_BASE_QUERY(
    'orderingCollectionMasterEntities',
    'PendingCollectionManagerBasicFragment',
    'CollectionMasterEntityFragment',
  )}
  ${CollectionMasterEntityFragment}
  ${PendingCollectionManagerBasicFragment}
`;

export const GET_PENDING_ORDERING_COLLECTION_MANAGERS_ACTIONS = gql`
  ${ORDERING_COLLECTION_MASTER_ENTITIES_BASE_QUERY(
    'orderingCollectionManagersActions',
    'PendingCollectionManagerActionsFragment',
  )}
  ${PendingCollectionManagerActionsFragment}
`;

export const NON_PRIORITY_RATIFICATION_MASTER_ENTITY = gql`
  query nonPriorityRatificationMasterEntity(
    $first: Int
    $offset: Int
    $globalFilter: String
    $orderBy: String
    $inRatification: Boolean
    $countryId: Int
    $debtor_RatificationPriority_Value: Int
  ) {
    getMasterEntities(
      first: $first
      offset: $offset
      globalFilter: $globalFilter
      inNonPriorityRatification: $inRatification
      orderBy: $orderBy
      countryId: $countryId
      debtor_RatificationPriority_Value: $debtor_RatificationPriority_Value
    ) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
      totalPages
      edges {
        cursor
        node {
          ...MasterEntityBasicField
          ...ReceiverDebtsSummary
          riskBlacklist {
            ...RiskListFields
          }
          debtor {
            id
            ratificator {
              ...UserIdentification
            }
          }
          invoicesInRatification: invoicesInNonPriorityRatification {
            ...InvoiceIdFields
            ratificationmanager {
              id
              actions {
                ...RatificationActionsBasicFields
                author {
                  ...UserIdentification
                }
              }
            }
          }
        }
      }
    }
  }
  ${ReceiverDebtsSummary}
  ${MasterEntityBasicField}
  ${InvoiceIdFields}
  ${UserIdentification}
  ${RatificationActionsBasicFields}
  ${RiskListFields}
`;

export const PRIORITY_RATIFICATION_MASTER_ENTITY = gql`
  query priorityRatificationMasterEntity(
    $first: Int
    $offset: Int
    $globalFilter: String
    $orderBy: String
    $inRatification: Boolean
    $countryId: Int
    $debtor_RatificationPriority_Value: Int
  ) {
    getMasterEntities(
      first: $first
      offset: $offset
      globalFilter: $globalFilter
      inPriorityRatification: $inRatification
      orderBy: $orderBy
      countryId: $countryId
      debtor_RatificationPriority_Value: $debtor_RatificationPriority_Value
    ) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
      totalPages
      edges {
        cursor
        node {
          ...MasterEntityBasicField
          ...ReceiverDebtsSummary
          riskBlacklist {
            ...RiskListFields
          }
          debtor {
            id
            ratifies
            ratificator {
              ...UserIdentification
            }
            ratificationPriority {
              ...CollectionPriorityFields
            }
            ratificationInformation {
              id
              comment
            }
          }
          invoicesInRatification: invoicesInPriorityRatification {
            ...InvoiceIdFields
            ratificationmanager {
              id
              actions {
                ...RatificationActionsBasicFields
                author {
                  ...UserIdentification
                }
              }
            }
          }
        }
      }
    }
  }
  ${ReceiverDebtsSummary}
  ${MasterEntityBasicField}
  ${InvoiceIdFields}
  ${UserIdentification}
  ${RatificationActionsBasicFields}
  ${CollectionPriorityFields}
  ${RiskListFields}
`;

export const GET_DEBTORS = gql`
  query getDebtors(
    $first: Int
    $offset: Int
    $globalFilter: String
    $orderBy: String
    $inCollection: Int
    $debtor_Isnull: Boolean
    $countryId: Int
  ) {
    getMasterEntities(
      first: $first
      offset: $offset
      globalFilter: $globalFilter
      inCollection: $inCollection
      orderBy: $orderBy
      debtor_Isnull: $debtor_Isnull
      countryId: $countryId
    ) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
      totalPages
      edges {
        cursor
        node {
          id
          ...MasterEntityBasicField
          riskBlacklist {
            ...RiskListFields
          }
          debtor {
            id
            ratifies
            ratificationInformation {
              id
              comment
            }
            ratificator {
              ...BasicUserFields
            }
            collector {
              ...BasicUserFields
            }
          }
        }
      }
    }
  }
  ${MasterEntityBasicField}
  ${BasicUserFields}
  ${RiskListFields}
`;

export const GET_COMPANY_EVALUATIONS = gql`
  query getCompanyEvaluations(
    $companyId: Int
    $hasEvaluatingPreoffer: Boolean
    $hasPendingDocuments: Boolean
    $first: Int
    $offset: Int
    $orderBy: String
    $currency: String
  ) {
    invoices(
      companyId: $companyId
      hasEvaluatingPreoffer: $hasEvaluatingPreoffer
      hasPendingDocuments: $hasPendingDocuments
      first: $first
      offset: $offset
      orderBy: $orderBy
      currency: $currency
    ) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
      totalPages
      edges {
        node {
          ...InvoiceIdFields
          ...InvoiceStatusFields
          ...InvoiceReferenceFields
          ...InvoiceAlertsFields
          ...InvoicePreofferEvaluationRequestTypeFields
          paymentDays
          operationDays
          invoiceloanmanager {
            ...InvoiceLoanManagerBasicField
            invoiceLoanRoster {
              ...InvoiceLoanRosterBasic
            }
          }
          documentfinancestateSet {
            ...DocumentFinanceStateFields
          }
          preoffer {
            id
            monthlyRate
            defaultRate
            retentionRate
            preofferevaluationrequest {
              ...PreofferEvaluationInfo
              riskautomaticevaluationlogSet {
                ...AutomaticRiskEvaluationResultFields
              }
              slaResolution {
                ...SLAResolutionFields
              }
            }
          }
          traces {
            ...TraceFields
          }
          dteType {
            ...InvoiceDteTypeFields
          }
          assignmentSet {
            ...AssignmentFields
          }
          amountWithIva {
            ...MoneyFields
          }
          complianceStatus {
            debtorApproved
            withinAvailableDays
            hasPaymentComplement
            rugInscribed
            __typename
          }
          documents {
            ...InvoiceDocumentsFields
          }
          company {
            id
            name
            rut
            masterEntity {
              ...MasterEntityBasicField
              evaluationComments {
                ...EvaluationCommentFields
              }
              riskBlacklist {
                ...RiskListFields
                comments
                createdAt
              }
              ratificationBlacklist {
                id
                active
              }
              ...CompanyDebtsSummary
            }
            currentIvaObservation {
              id
              observation
              createdAt
            }
            currentDicomObservation {
              id
              observation
              createdAt
            }
            currentCreditLine {
              id
              totalAmount {
                ...MoneyFields
              }
              approvedOverdraft {
                ...MoneyFields
              }
              creditUsed {
                ...MoneyFields
              }
            }
            executiveAssigned {
              ...UserIdentification
            }
            cessionsHistory {
              id
              cessionsNumber
              cessionsMoneyAmount
              historicCessionsNumber
              historicCessionsMoneyAmount
            }
            companyevaluationrestrictionsSet {
              ...CompanyEvaluationRestrictionMinimalFields
            }
            conservativeTycRequestingPlatform {
              id
              code
            }
          }
          receiver {
            ...MasterEntityBasicField
            debtorExternalRiskStatus{
              ...MasterEntityExternalRiskStatusFields
            }
            evaluationComments {
              ...EvaluationCommentFields
            }
            ...ReceiverDebtsSummary
            riskBlacklist {
              ...RiskListFields
              comments
              createdAt
            }
            ratificationBlacklist {
              id
              active
            }
            debtor {
              id
              ratifies
              ratificationInformation {
                id
                comment
              }
            }
            receiverCessionsHistory {
              id
              cessionsNumber
              cessionsMoneyAmount
              historicCessionsNumber
              historicCessionsMoneyAmount
            }
          }
          receiverDocumentRequested {
            id
            hesPercentage
            purchaseOrderPercentage
            invoicesAmount
          }
          riskEvaluationModel {
            id
            treeClientResult
            treeDebtorResult
            treeClientDebtorResult
            rasClientResult
            rasDebtorResult
            rasClientDebtorResult
            ratification
            reason
            clientTgrDatetimeObtained
            clientDebtorIsTailDebtor
            clientTailDebtorsExposureRatio
            ratificationResults {
              id
              createdAt
              modelProbability
              thresholdUsed
              ratificationResult
            }
            riskPredictionResults {
              id
              isAccepted
              isRejected
            }
            riskDefaultResults {
              id
              isAccepted
              isRejected
            }
          }
          companyReceiverCessionHistory {
            id
            cessionsNumber
            cessionsMoneyAmount
            historicCessionsNumber
            historicCessionsMoneyAmount
          }
          companyReceiverEvaluationComment {
            ...CompanyReceiverEvaluationCommentFields
          }
          ...InvoiceRequestingPlatformFields
          subline {
            ...CreditLineFields
            masterEntity {
              ...MasterEntityBasicField
            }
            debtor {
              ...MasterEntityBasicField
            }
            externalStatus {
              ...CreditLineInfoExternalRiskStatusFields
            }
          }
        }
      }
    }
  }
  ${SLAResolutionFields}
  ${InvoicePreofferEvaluationRequestTypeFields}
  ${InvoiceRequestingPlatformFields}
  ${ReceiverDebtsSummary}
  ${CompanyDebtsSummary}
  ${MoneyFields}
  ${InvoiceDocumentsFields}
  ${InvoiceLoanManagerBasicField}
  ${InvoiceLoanRosterBasic}
  ${MasterEntityBasicField}
  ${AssignmentFields}
  ${InvoiceIdFields}
  ${InvoiceStatusFields}
  ${InvoiceReferenceFields}
  ${DocumentFinanceStateFields}
  ${InvoiceDteTypeFields}
  ${UserIdentification}
  ${PreofferEvaluationInfo}
  ${AutomaticRiskEvaluationResultFields}
  ${InvoiceAlertsFields}
  ${EvaluationCommentFields}
  ${CompanyReceiverEvaluationCommentFields}
  ${TraceFields}
  ${CompanyEvaluationRestrictionMinimalFields}
  ${RiskListFields}
  ${CreditLineFields}
  ${MasterEntityExternalRiskStatusFields}
  ${CreditLineInfoExternalRiskStatusFields}
`;

export const GET_COMPANY_PENDING_DOCUMENTS_EVALUATIONS = gql`
  query invoices(
    $companyId: Int
    $hasEvaluatingPreoffer: Boolean
    $hasPendingDocuments: Boolean
    $first: Int
    $offset: Int
    $orderBy: String
  ) {
    invoices(
      companyId: $companyId
      hasEvaluatingPreoffer: $hasEvaluatingPreoffer
      hasPendingDocuments: $hasPendingDocuments
      first: $first
      offset: $offset
      orderBy: $orderBy
    ) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
      totalPages
      edges {
        node {
          ...InvoiceIdFields
          ...InvoiceStatusFields
          ...InvoiceReferenceFields
          ...InvoiceAlertsFields
          ...InvoiceRequestingPlatformFields
          ...InvoiceReviewDocumentsFields
          restrictedBy {
            ...CompanyEvaluationRestrictionMinimalFields
          }
          documentfinancestateSet {
            ...DocumentFinanceStateFields
          }
          preoffer {
            id
            monthlyRate
            defaultRate
            retentionRate
            preofferevaluationrequest {
              ...PreofferEvaluationInfo
            }
          }
          traces {
            ...TraceFields
          }
          uniqueDocumentFinanceState {
            ...DocumentFinanceStateFields
          }
          dteType {
            ...InvoiceDteTypeFields
          }
          assignmentSet {
            ...AssignmentFields
          }
          amountWithIva {
            ...MoneyFields
          }
          documents {
            ...InvoiceDocumentsFields
          }
          company {
            id
            name
            rut
            masterEntity {
              ...MasterEntityBasicField
            }
            executiveAssigned {
              ...UserIdentification
            }
            cessionsHistory {
              id
              cessionsNumber
              cessionsMoneyAmount
              historicCessionsNumber
              historicCessionsMoneyAmount
            }
            companyevaluationrestrictionsSet {
              ...CompanyEvaluationRestrictionMinimalFields
            }
          }
          receiver {
            ...MasterEntityBasicField
            ...ReceiverDebtsSummary
            receiverCessionsHistory {
              id
              cessionsNumber
              cessionsMoneyAmount
              historicCessionsNumber
              historicCessionsMoneyAmount
            }
          }
          receiverDocumentRequested {
            id
            hesPercentage
            purchaseOrderPercentage
            invoicesAmount
          }
          companyReceiverCessionHistory {
            id
            cessionsNumber
            cessionsMoneyAmount
            historicCessionsNumber
            historicCessionsMoneyAmount
          }
          offer {
            id
            transferCondition
          }
        }
      }
    }
  }
  ${ReceiverDebtsSummary}
  ${MoneyFields}
  ${InvoiceDocumentsFields}
  ${MasterEntityBasicField}
  ${AssignmentFields}
  ${InvoiceIdFields}
  ${InvoiceStatusFields}
  ${InvoiceReferenceFields}
  ${DocumentFinanceStateFields}
  ${InvoiceDteTypeFields}
  ${UserIdentification}
  ${PreofferEvaluationInfo}
  ${InvoiceAlertsFields}
  ${TraceFields}
  ${CompanyEvaluationRestrictionMinimalFields}
  ${InvoiceRequestingPlatformFields}
  ${InvoiceReviewDocumentsFields}
`;

export const GET_COMPANY_ORDERING_EVALUATIONS = gql`
  query getCompanyOrderingEvaluations(
    $companyId: Int!
    $inEvaluation: Boolean
    $first: Int
    $offset: Int
    $orderBy: String
    $inDebt: Boolean
  ) {
    purchaseOrders(
      companyId: $companyId
      inEvaluation: $inEvaluation
      inDebt: $inDebt
      first: $first
      offset: $offset
      orderBy: $orderBy
    ) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
      totalPages
      edges {
        node {
          id
          orderNumber
          company {
            id
            rut
            name
            masterEntity {
              id
              displayNationalIdentifier
              riskBlacklist {
                ...RiskListFields
                comments
                createdAt
              }
              evaluationComments {
                ...EvaluationCommentFields
              }
            }
            companyevaluationrestrictionsSet {
              ...CompanyEvaluationRestrictionMinimalFields
            }
          }
          purchaser {
            id
            rut
            name
            displayNationalIdentifier
            debtorExternalRiskStatus {
              ...MasterEntityExternalRiskStatusFields
            }
            riskBlacklist {
              ...RiskListFields
              comments
              createdAt
            }
            ratificationBlacklist {
              id
              active
            }
            evaluationComments {
              ...EvaluationCommentFields
            }
            ...ReceiverDebtsSummary
          }
          orderingsimulation {
            id
            invoiceDateToPay
            invoiceIssuedDate
            orderingMonthlyRate
            orderingDefaultRate
            orderingRetentionRate
            factoringMonthlyRate
            factoringDefaultRate
            factoringRetentionRate
            slaResolution {
              ...SLAResolutionFields
            }
          }
          totalAmount {
            ...MoneyFields
          }
          riskEvaluationModel {
            id
            treeClientResult
            treeDebtorResult
            treeClientDebtorResult
            rasClientResult
            rasDebtorResult
            rasClientDebtorResult
            ratification
            reason
            clientDebtorIsTailDebtor
            clientTailDebtorsExposureRatio
          }
          companyReceiverEvaluationComment {
            ...CompanyReceiverEvaluationCommentFields
          }
        }
      }
    }
  }
  ${SLAResolutionFields}
  ${ReceiverDebtsSummary}
  ${MoneyFields}
  ${EvaluationCommentFields}
  ${CompanyReceiverEvaluationCommentFields}
  ${CompanyEvaluationRestrictionMinimalFields}
  ${RiskListFields}
  ${MasterEntityExternalRiskStatusFields}
`;

export const GET_CUSTOMERS_BASICS = gql`
  query getCustomers(
    $first: Int
    $offset: Int
    $globalFilter: String
    $orderBy: String
    $inCollection: Int
    $inEvaluation: Boolean = false
    $company_Isnull: Boolean = false
    $withInvoiceRecommendation: [String] = []
    $loggedExecutive: Boolean = false
  ) {
    getMasterEntities(
      first: $first
      offset: $offset
      globalFilter: $globalFilter
      inCollection: $inCollection
      inEvaluation: $inEvaluation
      orderBy: $orderBy
      company_Isnull: $company_Isnull
      withInvoiceRecommendation: $withInvoiceRecommendation
      loggedExecutive: $loggedExecutive
    ) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
      totalPages
      edges {
        cursor
        node {
          id
          rut
          name
          displayNationalIdentifier
          company {
            rut
          }
        }
      }
    }
  }
`;

export const CONTRACT_MASTER_ENTITIES = gql`
  query contractMasterEntities(
    $id_In: [String]
    $first: Int
    $offset: Int
    $globalFilter: String
    $orderBy: String
    $loggedExecutive: Boolean
    $countryId: Int
    $companyExecutiveAssignedId: ID
    $farmingByCommercialType: String
    $inPendingFrameworkContract: Boolean
    $inCompletedFrameworkContract: Boolean
    $withMassiveSignatureContract: String
    $contractStatus: String
  ) {
    getMasterEntities(
      id_In: $id_In
      first: $first
      offset: $offset
      globalFilter: $globalFilter
      orderBy: $orderBy
      loggedExecutive: $loggedExecutive
      countryId: $countryId
      company_ExecutiveAssignedId: $companyExecutiveAssignedId
      huntingFarmingFilter: $farmingByCommercialType
      inPendingFrameworkContract: $inPendingFrameworkContract
      inCompletedFrameworkContract: $inCompletedFrameworkContract
      withMassiveSignatureContract: $withMassiveSignatureContract
      contractStatus: $contractStatus
    ) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
      totalPages
      edges {
        cursor
        node {
          ...MasterEntityBasicField
          riskBlacklist {
            id
          }
          country {
            id
            name
          }
          company {
            id
            executiveAssigned {
              ...UserIdentification
            }
            pendingFrameworkContractRequest {
              id
              status
              companyContractType
              createdAt
              generatedContract {
                id
              }
              currentContractUploadRequest {
                id
                status
                approvedRequest
                documentToUpload {
                  ...CompanyDocumentsFields
                }
              }
            }
            documents {
              ...CompanyDocumentsFields
            }
          }
        }
      }
    }
  }
  ${MasterEntityBasicField}
  ${CompanyDocumentsFields}
  ${UserIdentification}
`;

export const HISTORIC_CONTRACT_MASTER_ENTITIES = gql`
  query contractMasterEntities(
    $id_In: [String]
    $first: Int
    $offset: Int
    $globalFilter: String
    $orderBy: String
    $loggedExecutive: Boolean
    $countryId: Int
    $companyExecutiveAssignedId: ID
    $farmingByCommercialType: String
    $inPendingFrameworkContract: Boolean
    $inCompletedFrameworkContract: Boolean
    $withMassiveSignatureContract: String
  ) {
    getMasterEntities(
      id_In: $id_In
      first: $first
      offset: $offset
      globalFilter: $globalFilter
      orderBy: $orderBy
      loggedExecutive: $loggedExecutive
      countryId: $countryId
      company_ExecutiveAssignedId: $companyExecutiveAssignedId
      huntingFarmingFilter: $farmingByCommercialType
      inPendingFrameworkContract: $inPendingFrameworkContract
      inCompletedFrameworkContract: $inCompletedFrameworkContract
      withMassiveSignatureContract: $withMassiveSignatureContract
    ) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
      totalPages
      edges {
        cursor
        node {
          ...MasterEntityBasicField
          clientExternalRiskStatus {
            ...MasterEntityExternalRiskStatusFields
          }
          riskBlacklist {
            id
          }
          country {
            id
            name
          }
          company {
            id
            executiveAssigned {
              ...UserIdentification
              __typename
            }
            completedFrameworkContractRequest {
              id
              status
              companyContractType
              updatedAt
              internalSignatureSigned
              clientSignatureSummary {
                required
                signed
              }
              currentContractUploadRequest {
                id
                externalContract {
                  id
                  relationOriginalContracts {
                    id
                    massiveSignatureRequest {
                      id
                      status {
                        id
                        status
                      }
                    }
                  }
                }
              }
            }
            documents {
              ...CompanyDocumentsFields
            }
          }
        }
      }
    }
  }
  ${MasterEntityBasicField}
  ${CompanyDocumentsFields}
  ${UserIdentification}
  ${MasterEntityExternalRiskStatusFields}
`;

export const PENDING_CONTRACTS = gql`
  query getCompanyFrameworkContractRequests(
    $inPendingFrameworkContract: Boolean
    $companyId: ID
  ) {
    getCompanyFrameworkContractRequests(
      inPendingFrameworkContract: $inPendingFrameworkContract
      companyId: $companyId
    ) {
      edges {
        node {
          id
          status
          companyContractType
          currentContractUploadRequest {
            id
            status
            documentToUpload {
              ...CompanyDocumentsFields
            }
          }
          company {
            id
            masterEntity {
              ...MasterEntityBasicField
            }
          }
        }
      }
    }
  }
  ${MasterEntityBasicField}
  ${CompanyDocumentsFields}
`;

export const GET_COMPANY_FRAMEWORK_CONTRACT_REQUEST = gql`
  query getCompanyFrameworkContractRequest($id: Int!) {
    getCompanyFrameworkContractRequest(id: $id) {
      id
      status
      companyContractType
      currentContractUploadRequest {
        id
        status
        documentToUpload {
          ...CompanyDocumentsFields
        }
      }
      generatedContract {
        id
        fileFormat
        companyDocument {
          ...CompanyDocumentsFields
        }
      }
      company {
        id
        masterEntity {
          ...MasterEntityBasicField
        }
      }
    }
  }
  ${MasterEntityBasicField}
  ${CompanyDocumentsFields} 
`;
export const MASTER_ENTITIES_TERM_WHITELIST = gql`
  query whitelistMasterEntities(
    $first: Int
    $offset: Int
    $globalFilter: String
    $orderBy: String
    $countryId: Int
    $filterByTermWhitelist: String
  ) {
    getMasterEntities(
      first: $first
      offset: $offset
      globalFilter: $globalFilter
      orderBy: $orderBy
      countryId: $countryId
      filterByTermWhitelist: $filterByTermWhitelist
    ) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
      totalPages
      edges {
        cursor
        node {
          ...MasterEntityBasicField
        }
      }
    }
  }
  ${MasterEntityBasicField}
  ${CompanyDocumentsFields}
`;

export const DEBTOR_PAYMENT_BEHAVIOUR_ENUM = gql`
  query getDebtorPaymentBehaviourEnum {
    getDebtorPaymentBehaviourEnum {
      value
      label
    }
  }
`;

export const GET_MASTER_ENTITY_CONTACTS = gql`
  query GetMasterEntityContacts($masterEntityId: Int!) {
    getMasterEntityContacts(masterEntityId: $masterEntityId) {
      id
      name
      email
      phoneNumber
      position
    }
  }
`;

export const RELAY_NODE_INTERFACE = gql`
  query node($id: ID!) {
    node(id: $id) {
      ... on MasterEntityPublicInfoType {
        id
        name
        rut
        profilePictureUrl
      }
    }
  }
`;

export const FETCH_COMPANY_CTE = gql`
  mutation FetchCompanyCte($companyId: String!) {
    fetchCompanyCte(companyId: $companyId) {
      requestReceived
    }
  }
`;

export const FETCH_COMPANY_LEGAL_REPORT = gql`
  mutation fetchCompanyLegalReport($companyId: String!) {
    fetchCompanyLegalReport(companyId: $companyId) {
      requestReceived
    }
  }
`;

export const GET_COMPANY_ERUT = gql`
  mutation getCompanyERut($companyId: String!) {
    getCompanyErut(companyId: $companyId) {
      message
    }
  }
`;

export const FETCH_COMPANY_TGR = gql`
  mutation FetchCompanyTgr($companyId: String!) {
    fetchCompanyTgr(companyId: $companyId) {
      message
      severity
    }
  }
`;

export const GET_RISK_LIST_COMPANIES = gql`
  query masterEntities(
    $first: Int
    $offset: Int
    $globalFilter: String
    $riskListTypes: [String]
    $orderBy: String
    $countryId: Int
  ) {
    getMasterEntities(
      first: $first
      offset: $offset
      globalFilter: $globalFilter
      riskListTypes: $riskListTypes
      orderBy: $orderBy
      countryId: $countryId
    ) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
      totalPages
      edges {
        node {
          id
          rut
          name
          clientExternalRiskStatus {
            ...MasterEntityExternalRiskStatusFields
          }
          displayNationalIdentifier
          riskBlacklist {
            ...RiskListFields
            comments
            createdAt
            currentCategories {
              ...RiskListCategoryFields
            }
          }
          ...CompanyDebtsSummary
        }
      }
    }
  }
  ${CompanyDebtsSummary}
  ${RiskListFields}
  ${RiskListCategoryFields}
  ${MasterEntityExternalRiskStatusFields}
`;

export const ADD_COMPANIES_TO_RISK_LIST = gql`
  mutation addCompaniesToRiskList(
    $ruts: [String]!
    $comments: String
    $riskListType: RiskListTypeEnum!
    $categories: [RiskListCategoryEnum]
  ) {
    addCompaniesToRiskList(
      ruts: $ruts
      comments: $comments
      riskListType: $riskListType
      categories: $categories
    ) {
      warnings
      masterEntities {
        id
        riskBlacklist {
          ...RiskListFields
          comments
          createdAt
        }
      }
    }
  }
  ${RiskListFields}
`;

export const ADD_COMPANIES_TO_RISK_LIST_EXCEL = gql`
  mutation addCompaniesToRiskListExcel($file: Upload!) {
    addCompaniesToRiskListExcel(file: $file) {
      success
      errors
      warnings
      masterEntities {
        id
      }
    }
  }
`;

export const REMOVE_COMPANY_FROM_RISK_LIST = gql`
  mutation removeCompanyFromRiskList($ids: [String]!) {
    removeCompanyFromRiskList(ids: $ids) {
      success
    }
  }
`;

export const ADD_EVALUATION_COMMENT = gql`
  mutation addEvaluationComment(
    $masterEntity1: Int!
    $masterEntity2: Int
    $comment: String!
  ) {
    addEvaluationComment(
      masterEntity1: $masterEntity1
      masterEntity2: $masterEntity2
      comment: $comment
    ) {
      success
    }
  }
`;

export const CURRENT_BEHAVIOR_DETAILS = gql`
  query getCurrentBehavior($masterEntityId: Int!) {
    getMasterEntity(masterEntityId: $masterEntityId) {
      id
      name
      currentBehaviors {
        id
        createdAt
        totalDebt
        dateInform
        currentBehaviorDetails {
          id
          createdAt
          date
          directCastDebt
          indirectCastDebt
          comercialDebt
          consumerCreditDebt
          contingentCreditDebt
          mortgageCreditDebt
          directExpired3Months3YearsDebt
          directValidDebt
          indirectExpiredDebt
          indirectValidDebt
          financialDebt
          leasingDebt
          moroseDebt
          moroseLeasingDebt
          operationCPactDebt
          directExpiredDebt
          currentCreditLine
          comercialExpiredAmountDebt
          comercialValidAmountDebt
          consumeCreditNumberCreditors
          numberConsumeCreditEntities
        }
      }
    }
  }
`;

export const BUSINESS_PROFILE_DETAILS = gql`
  query getBusinessProfile($masterEntityId: Int!) {
    getMasterEntity(masterEntityId: $masterEntityId) {
      id
      name
      businessProfile {
        id
        createdAt
        tipoper
        economicActivity
        year
        cbrs
        clasification
        address
        activity
        morConsolidated
        labInformation
        protDocImp
        taxSituation
        numberWorkers
        businessName
        subActivity
        companySubtype
        companyType
        salesSection
        taxAppraisalAmount
        vehiclesAppraisal
        societies {
          id
          createdAt
          rut
          dv
          name
          capital
          beginDate
        }
        shareholders {
          id
          createdAt
          rut
          dv
          name
          participation
          dateVinculation
          dateInformation
          email
          type
          relatedMasterEntity {
            id
            isNaturalPersonRut
            riskBlacklist {
              ...RiskListFields
            }
            currentDicomPlatinum {
              id
              createdAt
              dicomAmount
              completedAt
            }
            currentDicom {
              id
              createdAt
              dicomAmount
              completedAt
            }
          }
        }
        details {
          id
          type
          createdAt
          year
          appraisal
          quality
          brand
          model
          chasisNumber
          motorNumber
          licensePlace
          ownerName
          ownerLastName
          ownerOrganization
          ownerRut
          ownerDv
          ranking
          carType
          use
        }
        judicialDetails {
          id
          createdAt
          type
          creditor
          clasification
          debtDays
          creditorDig
          date
          aclDate
          source
          glscodacteco
          identification
          process
          reference
          creditorRut
          url
        }
        taxAppraisal {
          id
          createdAt
          direction
          ubication
          taxAppraisal
          rol
          ranking
          quality
        }
      }
    }
  }
  ${RiskListFields}
`;

export const DICOM_DETAILS = gql`
  query getDicomDetails($masterEntityId: Int!) {
    getMasterEntity(masterEntityId: $masterEntityId) {
      id
      name
      dicoms {
        id
        createdAt
        validDicom
        dicomAmount
        completedAt
        dicomBulletins {
          id
          coinType
          amount
          market
          drawer
          debtType
          doctoNumber
          documentType
          expirationDate
          doctoEntryDate
        }
        shareholders {
          id
          createdAt
          rut
          dv
          name
          participation
          dateVinculation
          dateInformation
          email
          type
        }
        societies {
          id
          createdAt
          rut
          dv
          name
          capital
          beginDate
        }
        externalQueries {
          id
          createdAt
          businessName
          date
          use
          platform
        }
        dicomScores {
          id
          createdAt
          date
          score
        }
      }
    }
  }
`;

export const DICOM_PLATINUM_DETAILS = gql`
  query getDicomPlatinumDetails($masterEntityId: Int!) {
    getMasterEntity(masterEntityId: $masterEntityId) {
      id
      name
      dicomsPlatinum {
        id
        createdAt
        validDicom
        dicomAmount
        completedAt
        dicomBulletins {
          id
          coinType
          amount
          market
          drawer
          debtType
          doctoNumber
          documentType
          expirationDate
          doctoEntryDate
        }
        societies {
          id
          createdAt
          rut
          dv
          name
          capital
          beginDate
        }
        externalQueries {
          id
          createdAt
          businessName
          date
          use
          platform
        }
        dicomScores {
          id
          createdAt
          date
          score
        }
      }
    }
  }
`;

export const GET_RULE_RESULTS = gql`
  query getRuleResults($documentId: Int!, $modelName: String!) {
    getRuleResults(documentId: $documentId, modelName: $modelName) {
      id
      modelType
      rejects
      threshold
      name
      value
      prize
      logicalType
      labelName
      description
      valueParser
    }
  }
`;

export const GET_COMPANIES_BUREAUS = gql`
  query masterEntities(
    $first: Int
    $offset: Int
    $globalFilter: String
    $orderBy: String
    $countryId: Int
  ) {
    getMasterEntities(
      first: $first
      offset: $offset
      globalFilter: $globalFilter
      orderBy: $orderBy
      countryId: $countryId
    ) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
      totalPages
      edges {
        node {
          ...MasterEntityBasicField
          clientExternalRiskStatus {
            ...MasterEntityExternalRiskStatusFields
          }
          riskBlacklist {
            ...RiskListFields
          }
          currentDicomPlatinum {
            id
            createdAt
            dicomAmount
            completedAt
          }
          currentDicom {
            id
            createdAt
            dicomAmount
            completedAt
          }
          currentBusinessProfile {
            id
            createdAt
          }
          currentCurrentBehavior {
            id
            createdAt
          }
          debtor {
            id
            ratifies
          }
          ...CompanyDebtsSummary
        }
      }
    }
  }
  ${CompanyDebtsSummary}
  ${MasterEntityBasicField}
  ${RiskListFields}
  ${MasterEntityExternalRiskStatusFields}
`;

export const MASTER_ENTITY_PROFILE = gql`
  query masterEntity($masterEntityId: Int!) {
    getMasterEntity(masterEntityId: $masterEntityId) {
      ...MasterEntityBasicField
      currencies {
        id
        name
        isoCode
      }
      currentDicomPlatinum {
        id
        createdAt
      }
      currentDicom {
        id
        createdAt
      }
      country {
        id
        name
      }
      creditLinesByType(creditLineType: "FREE DISPOSAL") {
        ...CreditLineFields
      }
      debtor {
        id
        ratifies
        ratificator {
          ...UserIdentification
        }
        ratificationInformation {
          id
          comment
        }
      }
      evaluationComments {
        ...EvaluationCommentFields
      }
      contacts(first: 1, showHidden: false) {
        edges {
          node {
            id
            name
            position
            email
            phoneNumber
          }
        }
      }
      company {
        id
        rut
        name
        hasSiiCredentials
        hasInvoiceProviderCredentials
        hasDigitalCertificate
        hasSupplierPortalCredentials
        hasFrameworkContract
        frameworkContractStatus
        hasBuroAuthorizationFormDocument
        hasLendingAcceptedterms @client
        currentUserAcceptedTerms {
          id
          product
        }
        hasSiiCredentials @client
        hasInvoiceProviderCredentials @client
        hasSupplierPortalCredentials @client
        currentSurplusBalanceValue {
          ...MoneyFields
        }
        executiveAssigned {
          ...UserIdentification
        }
        masterEntity {
          ...MasterEntityBasicField
        }
        cessionsHistory {
          id
          cessionsNumber
          cessionsMoneyAmount
          historicCessionsNumber
          historicCessionsMoneyAmount
        }
        evaluationComments {
          id
          createdAt
          comments
          user {
            id
            firstName
            lastName
          }
        }
        documents {
          ...CompanyDocumentsFields
        }
        validCredentials {
          ...CredentialFields
        }
      }
    }
  }
  ${MoneyFields}
  ${MasterEntityBasicField}
  ${UserIdentification}
  ${CompanyDocumentsFields}
  ${CredentialFields}
  ${CreditLineFields}
  ${EvaluationCommentFields}
`;

export const MASTER_ENTITY_TERM_WHITELISTS = gql`
  query masterEntity(
    $masterEntityId: Int!
    $active: Boolean
    $first: Int
    $offset: Int
  ) {
    getMasterEntity(masterEntityId: $masterEntityId) {
      ...MasterEntityBasicField
      company {
        id
        name
        rut
        termWhitelistCompany(active: $active, first: $first, offset: $offset) {
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          totalCount
          totalPages
          edges {
            cursor
            node {
              ...TermWhitelistFields
            }
          }
        }
      }
    }
  }
  ${TermWhitelistFields}
  ${MasterEntityBasicField}
`;

export const COMPANY_LEGAL_REPRESENTATIVES = gql`
  query MasterEntity($masterEntityId: Int!) {
    getMasterEntity(masterEntityId: $masterEntityId) {
      id
      company {
        id
        legalRepresentative {
          id
          source
          phone
          isLegalRepresentative
          isPartner
          isCurrentSociety
          relatedPerson {
            id
            name
            rut
            isNaturalPersonRut
            currentDicomPlatinum {
              id
              createdAt
              dicomAmount
              completedAt
            }
            currentDicom {
              id
              createdAt
              dicomAmount
              completedAt
            }
            riskBlacklist {
              ...RiskListFields
            }
          }
        }
      }
    }
  }
  ${RiskListFields}
`;

export const GET_RESTRICTIONS_UNCREATED = gql`
  query getCompanyRestrictionUncreated($companyId: String!) {
    getCompanyRestrictionsUncreated(companyId: $companyId) {
      label
      value
      created
      disabledLabel
    }
  }
`;

export const GET_PENDING_COMPANY_RESTRICTIONS = gql`
  query GetPendingCompanyRestriction($companyId: String!) {
    getPendingCompanyRestrictions(companyId: $companyId) {
      id
      label
    }
  }
`;

export const GET_COMPANY_CONTRACT_GENERATION = gql`
  query getCompanyContractGeneration($companyId: Int!) {
    company(companyId: $companyId) {
      id
      name
      mainActivity
      address
      isNaturalPerson
      legalRepresentative {
        email
        label
        nationality
        profession
        civilStatus
        relatedPerson {
          id
          name
          rut
          displayNationalIdentifier
        }
      }
      lastPendingSignatureContractAnnex {
        id
        signatureDate
        companyContractName
      }
      endorsements {
        id
        label
        spouceLabel
        nationality
        profession
        civilStatus
        address
        spouceAddress
        spouceNationality
        spouceProfession
        spouceEmail
        email
        personeriaComments
        relatedPerson {
          id
          name
          rut
          displayNationalIdentifier
        }
        relatedSpouce {
          id
          name
          rut
          displayNationalIdentifier
        }
      }
      companyframeworkcontractregisterSet {
        id
      }
      legalReport {
        id
        societyType
        signatureCount
        factoringAvailable
        creditOperationsAvailable
        personeriaComments
        managementPeople {
          id
          source
          phone
          isLegalRepresentative
          isPartner
          isCurrentSociety
          relatedPerson {
            id
            name
            rut
            displayNationalIdentifier
            isNaturalPersonRut
            currentDicomPlatinum {
              id
              createdAt
              dicomAmount
              completedAt
            }
            currentDicom {
              id
              createdAt
              dicomAmount
              completedAt
            }
          }
        }
      }
      masterEntity {
        id
        rut
        displayNationalIdentifier
        creditLinesByType(creditLineType: "FREE DISPOSAL") {
          id
          currentLimit {
            id
            newLimit {
              amount
            }
          }
        }
      }
      pendingFrameworkContractRequest {
        id
        status
        restriction {
          id
          contractType
          endorser {
            email
            label
            nationality
            profession
            civilStatus
            relatedPerson {
              id
              name
              rut
              displayNationalIdentifier
            }
          }
        }
      }
    }
  }
`;

export const GET_COMPANY_CONTRACT_GENERATION_SIMPLE = gql`
  query getCompanyContractGeneration($companyId: Int!) {
    company(companyId: $companyId) {
      id
      rut
      name
      lastPendingSignatureContractAnnex {
        id
        signatureDate
        companyContractName
        publicDeedNumber
        publicDeedDate
        notaryName
        notaryNumber
        notaryAddress
      }
      masterEntity {
        id
        name
        rut
        displayNationalIdentifier
      }
      bankAccounts {
        id
        active
        bankName
        accountType
        accountNumber
        currency {
          id
          isoCode
        }
      }
      legalRepresentative {
        id
        source
        relatedPerson {
          id
          name
          displayNationalIdentifier
          address
        }
      }
      endorsements {
        id
        address
        relatedPerson {
          id
          name
          rut
          displayNationalIdentifier
          address
        }
      }
    }
  }
`;

export const GET_COUNTRIES = gql`
  query getCountries {
    getCountries {
      id
      name
    }
  }
`;

export const PUBLIC_MASTER_ENTITIES = gql`
  query publicMasterEntities($globalFilter: String!, $countryId: Int) {
    publicMasterEntities(globalFilter: $globalFilter, countryId: $countryId) {
      ...MasterEntityPublicInfoType
    }
  }
  ${MasterEntityPublicInfoType}
`;

export const COMPANY_REQUIRED_DOCUMENT_TYPES = gql`
  query getCompanyRequiredDocumentTypes($countryId: String!) {
    getCompanyRequiredDocumentTypes(countryId: $countryId) {
      id
      name
      shortName
      code
    }
  }
`;

export const COMPANY_DOCUMENT_TYPES = gql`
  query companyDocumentTypes($countryId: String!) {
    companyDocumentTypes(countryId: $countryId) {
      id
      name
      shortName
      code
    }
  }
`;

export const MASTER_ENTITY_PROFILE_AS_DEBTOR = gql`
  query masterEntityProfileAsDebtor($masterEntityId: Int!) {
    staffMasterEntity(masterEntityId: $masterEntityId) {
      ...MasterEntityBasicField
      invoicesActualPaymentTerm
      invoicesAgreedPaymentTermAverage
      avgRatificationDuration
      receiverCessionsHistory {
        id
        historicCessionsNumber
        historicCessionsMoneyAmount
      }
    }
  }
  ${MasterEntityBasicField}
`;

export const STAFF_MASTER_ENTITY_PROFILE = gql`
  query staffMasterEntityProfile($masterEntityId: Int!) {
    staffMasterEntity(masterEntityId: $masterEntityId) {
      ...MasterEntityBasicField
      country {
        id
        name
      }
      creditLinesByType(creditLineType: "FREE DISPOSAL") {
        ...CreditLineFields
      }
      debtor {
        id
        ratificator {
          ...UserIdentification
        }
        ratificationInformation {
          id
          comment
        }
        ratifies
      }
      evaluationComments {
        ...EvaluationCommentFields
      }
      contacts(first: 1, showHidden: false) {
        edges {
          node {
            id
            name
            position
            email
            phoneNumber
          }
        }
      }
      company {
        id
        rut
        name
        hasSiiCredentials
        hasInvoiceProviderCredentials
        hasDigitalCertificate
        hasSupplierPortalCredentials
        hasFrameworkContract
        frameworkContractStatus
        hasBuroAuthorizationFormDocument
        hasLendingAcceptedterms @client
        currentUserAcceptedTerms {
          id
          product
        }
        hasSiiCredentials @client
        hasInvoiceProviderCredentials @client
        hasSupplierPortalCredentials @client
        currentSurplusBalanceValue {
          ...MoneyFields
        }
        executiveAssigned {
          ...UserIdentification
        }
        masterEntity {
          ...MasterEntityBasicField
        }
        cessionsHistory {
          id
          cessionsNumber
          cessionsMoneyAmount
          historicCessionsNumber
          historicCessionsMoneyAmount
        }
        evaluationComments {
          id
          createdAt
          comments
          user {
            id
            firstName
            lastName
          }
        }
        documents {
          ...CompanyDocumentsFields
        }
        validCredentials {
          ...CredentialFields
        }
      }
    }
  }
  ${MoneyFields}
  ${MasterEntityBasicField}
  ${UserIdentification}
  ${CompanyDocumentsFields}
  ${CredentialFields}
  ${CreditLineFields}
  ${EvaluationCommentFields}
`;

export const CREDIT_LINE_MOVEMENTS_OF_OBJECT = gql`
  query creditLineMovementsOfObject(
    $first: Int
    $offset: Int
    $globalAppId: String!
    $orderBy: String
  ) {
    creditLineMovementsOfObject(
      globalAppId: $globalAppId
      first: $first
      offset: $offset
      orderBy: $orderBy
    ) {
      totalCount
      totalPages
      edges {
        cursor
        node {
          ...CreditLineMovementFields
          creditLine {
            id
            masterEntity {
              ...MasterEntityBasicField
            }
          }
          creditlinetransferSet {
            ...CreditLineTransferTypeFields
          }
        }
      }
    }
  }
  ${CreditLineMovementFields}
  ${MasterEntityBasicField}
  ${CreditLineTransferTypeFields}
`;

export const CREDIT_LINE_EVALUATION_REQUESTS = gql`
  query creditLineEvaluationRequests(
    $first: Int
    $offset: Int
    $globalFilter: String
    $creditlines_CreditLineEvaluationRequests_Status: String
    $creditlines_CreditLineEvaluationRequests_Status_In: [String]
    $countryId: Int
    $currency: String
    $id_In: [String]
  ) {
    getMasterEntities(
      first: $first
      offset: $offset
      globalFilter: $globalFilter
      creditlines_CreditLineEvaluationRequests_Status: $creditlines_CreditLineEvaluationRequests_Status
      creditlines_CreditLineEvaluationRequests_Status_In: $creditlines_CreditLineEvaluationRequests_Status_In
      countryId: $countryId
      id_In: $id_In
      creditlineEvaluationRequestCurrency: $currency
      distinct: true
    ) {
      totalPages
      totalCount
      pageInfo {
        startCursor
        hasNextPage
        hasPreviousPage
      }
      edges {
        cursor
        node {
          ...MasterEntityBasicField
          clientExternalRiskStatus {
            ...MasterEntityExternalRiskStatusFields
          }
          riskBlacklist {
            ...RiskListFields
          }
          creditLine(currency: $currency) {
            ...CreditLineFields
            creditLineEvaluationRequests {
              ...CreditLineEvaluationRequestFields
              creditlinesuggestionSet {
                ...CreditLineSuggestionTypeFields
              }
              creditlineevaluationvariablesSet {
                ...CreditlineEvaluationVariablesTypeFields
                creditlineevaluationindicatorsSet {
                  ...CreditlineEvaluationIndicatorsTypeFields
                }
              }
            }
          }
          ...CompanyDebtsSummary
        }
      }
    }
  }
  ${CompanyDebtsSummary}
  ${CreditLineFields}
  ${MasterEntityBasicField}
  ${CreditLineEvaluationRequestFields}
  ${CreditLineSuggestionTypeFields}
  ${CreditlineEvaluationVariablesTypeFields}
  ${CreditlineEvaluationIndicatorsTypeFields}
  ${RiskListFields}
  ${MasterEntityExternalRiskStatusFields}
`;

export const GET_PROCESSING_COMPANIES = gql`
  query masterEntities(
    $first: Int
    $offset: Int
    $globalFilter: String
    $orderBy: String
    $countryId: Int
    $company_Invoice_Status: String
    $distinct: Boolean
  ) {
    getMasterEntities(
      first: $first
      offset: $offset
      globalFilter: $globalFilter
      orderBy: $orderBy
      countryId: $countryId
      company_Invoice_Status: $company_Invoice_Status
      distinct: $distinct
    ) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
      totalPages
      edges {
        node {
          id
          rut
          name
          displayNationalIdentifier
          currencies {
            id
            isoCode
          }
          riskBlacklist {
            ...RiskListFields
          }
        }
      }
    }
  }
  ${RiskListFields}
`;

export const GET_MASTER_ENTITY_RESTRICTIONS = gql`
  query getMasterEntityRestrictions(
    $masterEntityId: Int!
    $statuses: [String]!
  ) {
    getMasterEntity(masterEntityId: $masterEntityId) {
      id
      rut
      name
      displayNationalIdentifier
      company {
        id
        name
        documents {
          ...CompanyDocumentsFields
        }
        filteredRestrictions(statuses: $statuses) {
          ...CompanyRestrictions
        }
      }
    }
  }
  ${CompanyDocumentsFields}
  ${CompanyRestrictions}
`;

export const GET_SETTLEMENT_OPERATIONS = gql`
  query getSettlementOperations(
    $companyId: Int!
    $first: Int
    $offset: Int
    $globalFilter: String
    $orderBy: String
    $operationCreatedAt_Gte: DateTime
    $operationCreatedAt_Lte: DateTime
  ) {
    getSettlementOperations(companyId: $companyId
      first: $first
      offset: $offset
      globalFilter: $globalFilter
      orderBy: $orderBy
      operationCreatedAt_Gte: $operationCreatedAt_Gte
      operationCreatedAt_Lte: $operationCreatedAt_Lte
    ) {
      edges {
        node {
          id
          operationId
          operationDate
          pdfFile
          invoicesCount
          totalAmountTransferred {
            ...MoneyFields
          }
        }
      }
      totalCount
      totalPages
    }
  }
  ${MoneyFields}
`;

export const CREDIT_LINE_MASTER_ENTITIES = gql`
  query creditLineMasterEntities(
    $first: Int
    $offset: Int
    $globalFilter: String
    $orderBy: String
    $countryId: Int
    $hasSublines: Boolean
  ) {
    getMasterEntities(
      first: $first
      offset: $offset
      globalFilter: $globalFilter
      orderBy: $orderBy
      countryId: $countryId
      hasSublines: $hasSublines
    ) {
      totalCount
      edges {
        cursor
        node {
          ...MasterEntityBasicField
          clientExternalRiskStatus {
            ...MasterEntityExternalRiskStatusFields
          }
          creditLinesByType(creditLineType: "FREE DISPOSAL") {
            ...CreditLineFields
          }
          currencies {
            id
            name
            isoCode
          }
          hasSubline
        }
      }
    }
  }
  ${MasterEntityBasicField}
  ${CreditLineFields}
  ${MasterEntityExternalRiskStatusFields}
`;
